import { validatePhoneNumberLength } from 'libphonenumber-js';
import {
  type Country,
  isPossiblePhoneNumber,
  isSupportedCountry,
  isValidPhoneNumber,
} from 'react-phone-number-input';

export const FALLBACK_PHONE_COUNTRY: Country = 'US';

export const COUNTRY_CODE_HINT = 'Outside the US? Include your country code';

export const INVALID_PHONE_MESSAGE = 'Check the number and its country code';

/**
 * Below this many digits a number may still be mid-typing, so we never guess
 * a different country for it. Ten is a full US number without the leading 1.
 */
const MIN_DIGITS_TO_DETECT_COUNTRY = 10;

/**
 * Picks the phone country from the browser language, so `en-GB` starts on
 * the UK and `de-AT` on Austria. Language tags without a region (`de`) or
 * with a non-country region (`es-419`) are skipped.
 */
export const getBrowserPhoneCountry = (
  languages: readonly string[] = typeof navigator === 'undefined'
    ? []
    : navigator.languages?.length
      ? navigator.languages
      : [navigator.language],
): Country => {
  for (const language of languages) {
    const region = language?.split(/[-_]/)[1]?.toUpperCase();

    if (region && isSupportedCountry(region)) {
      return region;
    }
  }

  return FALLBACK_PHONE_COUNTRY;
};

const getDigits = (rawInput: string) => rawInput.replace(/\D/g, '');

/**
 * When a guest types a number with its country code but no `+`
 * (`44 20 7946 0999`), returns it in E.164 (`+442079460999`).
 *
 * Returns undefined when the input already starts with `+`, is a valid number
 * for the selected country (so real US numbers never switch), is too short to
 * judge, or isn't a valid number with a `+` in front either.
 */
export const getInternationalNumberWithoutPlus = (
  rawInput: string,
  currentValue: string | undefined,
): string | undefined => {
  if (rawInput.trim().startsWith('+')) {
    return undefined;
  }

  if (currentValue && isValidPhoneNumber(currentValue)) {
    return undefined;
  }

  const digits = getDigits(rawInput);

  if (digits.length < MIN_DIGITS_TO_DETECT_COUNTRY || digits.startsWith('0')) {
    return undefined;
  }

  const candidate = `+${digits}`;

  return isValidPhoneNumber(candidate) ? candidate : undefined;
};

/**
 * True when the number has more digits than its country allows and adding a
 * `+` in front wouldn't make it a plausible international number either, so
 * the guest is told right away instead of the field silently stopping.
 * Fields that don't detect country codes without a `+` flag any number that
 * is too long for its country.
 */
export const isPhoneNumberTooLong = (
  rawInput: string,
  value: string | undefined,
  { detectsCountryCodeWithoutPlus = true } = {},
): boolean => {
  if (!value || validatePhoneNumberLength(value) !== 'TOO_LONG') {
    return false;
  }

  const digits = getDigits(rawInput);

  return (
    !detectsCountryCodeWithoutPlus ||
    rawInput.trim().startsWith('+') ||
    digits.startsWith('0') ||
    !isPossiblePhoneNumber(`+${digits}`)
  );
};
